/**
 * Geometry and Color Helpers for the Interactive Kostolany Egg Diagram
 */

import type { EggPhase, EggZone } from '../types';

export interface EggNodeCoordinate {
  x: number;
  y: number;
  labelX: number;
  labelY: number;
  angleDeg: number;
  phase: EggPhase;
  titleZh: string;
  crowdRule: string;
  zone: EggZone;
  color: string;
}

/**
 * Returns Chinese subtitle for each Kostolany egg phase.
 */
export function getPhaseChineseTitle(phase: EggPhase): string {
  switch (phase) {
    case 'B3':
      return '跌市末段 (恐慌見底)';
    case 'A1':
      return '升市初段 (築底吸籌)';
    case 'A2':
      return '升市中段 (放量主升)';
    case 'A3':
      return '升市末段 (狂熱派發)';
    case 'B1':
      return '跌市初段 (頭部做頭)';
    case 'B2':
      return '跌市中段 (破線下跌)';
    default:
      return phase;
  }
}

/**
 * Returns Kostolany crowd / volume principle description for each phase.
 */
export function getPhaseCrowdRule(phase: EggPhase): string {
  switch (phase) {
    case 'B3':
      return '成交量爆量見底，堅定投資者接手怯懦者籌碼';
    case 'A1':
      return '成交量縮至極低，主力靜悄悄築底吸籌';
    case 'A2':
      return '價量齊揚，隨行者陸續跟進，趨勢確立';
    case 'A3':
      return '大眾瘋狂追價，成交量極大，主力悄悄賣出';
    case 'B1':
      return '量縮盤跌做頭，隨行者不願相信轉折已至';
    case 'B2':
      return '跌勢加劇，多殺多引發停損，流動性枯竭';
    default:
      return '';
  }
}

/**
 * Maps egg phase to its functional risk zone.
 */
export function getZoneForPhase(phase: EggPhase): EggZone {
  switch (phase) {
    case 'B3':
    case 'A1':
      return 'BUY';
    case 'A2':
    case 'B2':
      return 'WAIT';
    case 'A3':
    case 'B1':
      return 'SELL';
  }
}

/**
 * Returns the Taiwan market semantic color for each phase.
 * Buy / Bullish = Red (#EF4444)
 * Wait / Transition = Amber (#F59E0B)
 * Sell / Bearish = Emerald Green (#10B981)
 */
export function getPhaseColor(phase: EggPhase): string {
  switch (phase) {
    case 'B3':
    case 'A1':
      return '#EF4444'; // Red (Buy Zone)
    case 'A2':
      return '#EF4444'; // Red (Bullish Markup)
    case 'B2':
      return '#F59E0B'; // Amber (Defensive / Wait)
    case 'A3':
      return '#10B981'; // Green (Overheated / Sell)
    case 'B1':
      return '#10B981'; // Green (Distribution / Sell Zone)
    default:
      return '#9CA3AF';
  }
}

/**
 * Computes exact Cartesian coordinates on an upright ellipse for all 6 egg phases.
 * Top point = 270 deg / Top Apex, Bottom point = 90 deg / Bottom Apex.
 */
export function calculateEggCoordinates(
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  phase: EggPhase
): EggNodeCoordinate {
  let angleDeg = 0;
  let labelOffsetX = 0;
  let labelOffsetY = 0;

  switch (phase) {
    // Left Side: Upward Bullish Trend (Y -> X)
    case 'A1':
      angleDeg = 130; // Lower left (Buying: Phase of correction / accumulation)
      labelOffsetX = -75;
      labelOffsetY = 20;
      break;
    case 'A2':
      angleDeg = 180; // Left middle (Waiting: Phase of accompaniment / markup)
      labelOffsetX = -75;
      labelOffsetY = 5;
      break;
    case 'A3':
      angleDeg = 230; // Upper left (Selling: Phase of exaggeration / euphoria)
      labelOffsetX = -75;
      labelOffsetY = -15;
      break;

    // Right Side: Downward Bearish Trend (X -> Y)
    case 'B1':
      angleDeg = 310; // Upper right (Selling: Phase of correction / distribution)
      labelOffsetX = 20;
      labelOffsetY = -15;
      break;
    case 'B2':
      angleDeg = 0; // Right middle (Waiting: Phase of accompaniment / markdown)
      labelOffsetX = 20;
      labelOffsetY = 5;
      break;
    case 'B3':
      angleDeg = 50; // Lower right (Buying: Phase of exaggeration / capitulation)
      labelOffsetX = 20;
      labelOffsetY = 20;
      break;
  }

  const rad = (angleDeg * Math.PI) / 180;
  const x = cx + rx * Math.cos(rad);
  const y = cy + ry * Math.sin(rad);

  return {
    x,
    y,
    labelX: x + labelOffsetX,
    labelY: y + labelOffsetY,
    angleDeg,
    phase,
    titleZh: getPhaseChineseTitle(phase),
    crowdRule: getPhaseCrowdRule(phase),
    zone: getZoneForPhase(phase),
    color: getPhaseColor(phase),
  };
}
